import { Component, h } from 'preact'
import { Group, GroupItem, membershipTypes, ORIGIN_GROUP_WIDGET } from '../groups-destination/GroupOverview.sync'
import Button from '../../control-components/ui-components/Button'
import Axios from 'axios'
import { isExternalLink } from '../../utils/url'
import Message from '../design-system-components/message'
import Phrase from '../../libs/translationsUtils/phrase'
import { GetServerSideProps } from '../../server-middleware/context'
import { z } from 'zod'

interface Props {
  title: string
  groups: Array<{id?: number}>
  buttonLabel?: string
  buttonURL?: string
}

interface State {
  groups: Group[]
  successMessageFor: null | membershipTypes
  joinedGroupTitle: string
}

export const widgetName = 'groups'

export const config = z.object({
  title: z.string(),
  groups: z.array(z.object({
    id: z.number()
  })),
  buttonLabel: z.string(),
  buttonURL: z.string()
})

type Config = z.infer<typeof config>

export const getServerSideProps: GetServerSideProps<Config, Props> = async (_, config) => {
  return {
    title: config.title,
    groups: config.groups,
    buttonLabel: config.buttonLabel,
    buttonURL: config.buttonURL
  }
}

export class GroupsWidgetConfigurator extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    this.state = {
      groups: [],
      successMessageFor: null,
      joinedGroupTitle: ''
    }
  }

  async componentDidMount () {
    const res = await Axios.get('/getGroups', {
      params: {
        ids: this.props.groups.map(group => group.id)
      }
    })
    if (res.data && (res.data.myGroups.length || res.data.otherGroups.length)) {
      const newGroupList = this.props.groups.map((group) => {
        const indexOfMyList = res.data.myGroups.findIndex((myGroup: Group) => myGroup.id === group.id)
        if (indexOfMyList !== -1) {
          return { ...res.data.myGroups[indexOfMyList], ...{ isUserMember: true } }
        }

        const indexOfOtherList = res.data.otherGroups.findIndex((myGroup: Group) => myGroup.id === group.id)
        if (indexOfOtherList === -1) return null

        return { ...res.data.otherGroups[indexOfOtherList], ...{ isUserMember: false } }
      })
      this.setState({ groups: newGroupList.filter(g => g) })
    }
  }

  render ({ title, buttonLabel, buttonURL }: Props, { groups, successMessageFor, joinedGroupTitle }: State) {
    const externalLink = typeof window !== 'undefined' ? isExternalLink(buttonURL || '', window.location) : false

    if (!groups.length) {
      return null
    }

    return (
      <section
        className='full-width Template-brand-featured homepage-widget-wrapper groups-container qa-groups-container custom-groups'
      >
        {
          successMessageFor !== null
            ? (
              <Message
                variant='success'
                onDismiss={() => this.setState({ successMessageFor: null })}
                timer={5000}
              >
                <Phrase p={successMessageFor === 'joined' ? 'group.joined.successfully' : 'group.join.request.is.sent'} count={{ g: joinedGroupTitle }} />
              </Message>
              )
            : null
        }
        <div className='sitewidth'>
          <div className='col'>
            {
              title ? <h3 className='widget__heading'>{title}</h3> : null
            }
            <div className='group-overview'>
              {
                groups.map((group) => (
                  <GroupItem
                    key={group.id}
                    displayUserStatus
                    onMembershipStatusChanged={(newMembershipStatus: membershipTypes) => (
                      this.setState({
                        successMessageFor: newMembershipStatus
                      })
                    )}
                    isEvenCount={groups.length === 2 || groups.length === 4}
                    {...group}
                    origin={ORIGIN_GROUP_WIDGET}
                    showGroupMeta={false}
                  />
                ))
              }
            </div>
            {buttonLabel && buttonURL &&
              <a
                class='featured-topics__show-more-button'
                href={buttonURL}
                target={`${externalLink ? '_blank' : '_self'}`}
                rel={`${externalLink ? 'noreferrer noopener' : ''}`}
              >
                <Button
                  title={buttonLabel}
                />
              </a>}
          </div>
        </div>
      </section>
    )
  }
}

export default GroupsWidgetConfigurator
